import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { 
  FiBarChart, 
  FiTrendingUp, 
  FiTrendingDown, 
  FiChevronDown,
  FiChevronRight
} = FiIcons;

const CustomerVarianceReport = ({ customerId }) => {
  const [expandedGroups, setExpandedGroups] = useState(new Set(['abiraterone-acetate']));

  const toggleGroup = (groupId) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  // Mock variance data
  const varianceData = [
    {
      id: 'abiraterone-acetate',
      productName: 'Abiraterone Acetate Tablets',
      products: [
        {
          ndc: '72578-001-30',
          strength: '250mg',
          forecastedVolume: 35000,
          actualVolume: 38500,
          variance: 10.0,
          status: 'Above Forecast'
        },
        {
          ndc: '72578-001-60',
          strength: '500mg',
          forecastedVolume: 12000,
          actualVolume: 10800,
          variance: -10.0,
          status: 'Below Forecast'
        }
      ]
    },
    {
      id: 'atorvastatin',
      productName: 'Atorvastatin Tablets',
      products: [
        {
          ndc: '72578-002-90',
          strength: '10mg',
          forecastedVolume: 72000,
          actualVolume: 74500,
          variance: 3.5,
          status: 'On Target'
        },
        {
          ndc: '72578-002-91',
          strength: '20mg',
          forecastedVolume: 63000,
          actualVolume: 59800,
          variance: -5.1,
          status: 'Slight Underperformance'
        }
      ]
    },
    {
      id: 'rosuvastatin',
      productName: 'Rosuvastatin Tablets',
      products: [
        {
          ndc: '72578-003-30',
          strength: '5mg',
          forecastedVolume: 28000,
          actualVolume: 31200,
          variance: 11.4,
          status: 'Above Forecast'
        },
        {
          ndc: '72578-003-31',
          strength: '10mg',
          forecastedVolume: 22000,
          actualVolume: 20500,
          variance: -6.8,
          status: 'Below Forecast'
        }
      ]
    }
  ];

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getVarianceColor = (variance) => {
    if (variance > 5) return { text: 'text-green-600', bg: 'bg-green-100', icon: FiTrendingUp };
    if (variance < -5) return { text: 'text-red-600', bg: 'bg-red-100', icon: FiTrendingDown };
    return { text: 'text-gray-600', bg: 'bg-gray-100', icon: FiTrendingUp };
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'Above Forecast': { color: 'text-green-800', bg: 'bg-green-100' },
      'Below Forecast': { color: 'text-red-800', bg: 'bg-red-100' },
      'On Target': { color: 'text-blue-800', bg: 'bg-blue-100' },
      'Slight Underperformance': { color: 'text-yellow-800', bg: 'bg-yellow-100' }
    };
    return statusMap[status] || { color: 'text-gray-800', bg: 'bg-gray-100' };
  };

  return (
    <div className="space-y-6">
      {/* Product Variance Report */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden"
      >
        <div className="p-6 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <SafeIcon icon={FiBarChart} className="text-blue-600" />
            Product Variance Report
          </h3>
          <p className="text-gray-600 mt-1">
            Forecasted vs actual volume performance by product
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Product Name</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">NDC</th>
                <th className="text-left py-4 px-6 font-semibold text-gray-900">Strength</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Forecasted Volume</th>
                <th className="text-right py-4 px-6 font-semibold text-gray-900">Actual Volume</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900">Variance %</th>
                <th className="text-center py-4 px-6 font-semibold text-gray-900">Status</th>
              </tr>
            </thead>
            <tbody>
              {varianceData.map((group) => (
                <React.Fragment key={group.id}>
                  {/* Group Header Row */}
                  <tr className="bg-blue-50 border-b border-gray-200 cursor-pointer hover:bg-blue-100 transition-colors">
                    <td 
                      colSpan="7" 
                      className="py-4 px-6"
                      onClick={() => toggleGroup(group.id)}
                    >
                      <div className="flex items-center gap-3">
                        <SafeIcon 
                          icon={expandedGroups.has(group.id) ? FiChevronDown : FiChevronRight}
                          className="text-blue-600"
                        />
                        <span className="font-semibold text-blue-900">{group.productName}</span>
                      </div>
                    </td>
                  </tr>
                  
                  {/* Product Detail Rows */}
                  {expandedGroups.has(group.id) && group.products.map((product, index) => {
                    const varianceColor = getVarianceColor(product.variance);
                    const statusColor = getStatusColor(product.status);
                    
                    return (
                      <tr 
                        key={`${group.id}-${index}`}
                        className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                      >
                        <td className="py-4 px-6 pl-12">
                          <span className="text-sm text-gray-600">→ {group.productName}</span>
                        </td>
                        <td className="py-4 px-6">
                          <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                            {product.ndc}
                          </span>
                        </td>
                        <td className="py-4 px-6">
                          <span className="font-medium text-gray-900">{product.strength}</span>
                        </td>
                        <td className="py-4 px-6 text-right text-gray-700">
                          {formatNumber(product.forecastedVolume)} packs
                        </td>
                        <td className="py-4 px-6 text-right font-medium text-gray-900">
                          {formatNumber(product.actualVolume)} packs
                        </td>
                        <td className="py-4 px-6 text-center">
                          <div className="flex items-center justify-center gap-1">
                            <SafeIcon icon={varianceColor.icon} className={`text-sm ${varianceColor.text}`} />
                            <span className={`font-medium ${varianceColor.text}`}>
                              {product.variance > 0 ? '+' : ''}{product.variance}%
                            </span>
                          </div>
                        </td>
                        <td className="py-4 px-6 text-center">
                          <span className={`inline-flex px-3 py-1 rounded-full text-xs font-medium ${statusColor.bg} ${statusColor.color}`}>
                            {product.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Summary Analytics */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6"
      >
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">3</p>
              <p className="text-sm text-gray-600">Above Forecast</p>
            </div>
          </div>
          <p className="text-gray-700 text-sm">Products exceeding expectations</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrendingDown} className="text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">3</p>
              <p className="text-sm text-gray-600">Below Forecast</p>
            </div>
          </div>
          <p className="text-gray-700 text-sm">Products underperforming</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiBarChart} className="text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">+0.6%</p>
              <p className="text-sm text-gray-600">Average Variance</p>
            </div>
          </div>
          <p className="text-gray-700 text-sm">Overall performance metric</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">234K</p>
              <p className="text-sm text-gray-600">Total Volume</p>
            </div>
          </div>
          <p className="text-gray-700 text-sm">Actual volume delivered</p>
        </div>
      </motion.div>
    </div>
  );
};

export default CustomerVarianceReport;